import { useCallback, useEffect, useMemo, useState } from "react";
import { get, buildQuery } from "../api/axios";

// حد أعلى لعدد الصفحات التي يسحبها التصدير، حماية من طلبات لا تنتهي
const MAX_EXPORT_PAGES = 500;

// عدد الأسطر المعروضة في صفحة الجدول الواحدة
export const DEFAULT_PER_PAGE = 10;

// التصدير يسحب كل السجلات، فيطلب دفعات كبيرة لتقليل عدد الطلبات
const EXPORT_PER_PAGE = 100;

/**
 * حالة جدول لوحة التحكم: الجلب والبحث والترتيب والتنقّل بين الصفحات.
 * تُمرَّر النتيجة كما هي إلى <DataTable table={...} />.
 *
 * @param {string} endpoint مسار الـ API بدون بارامترات (مثال: "banks")
 * @param {object} [options]
 * @param {object} [options.params] بارامترات إضافية ثابتة أو فلاتر الصفحة
 * @param {boolean} [options.enabled=true] لتأجيل الجلب حتى تجهز شروطه
 * @param {number} [options.perPage] عدد الأسطر في الصفحة (الافتراضي 10)
 * @param {(res: object) => {rows: Array, meta: object}} [options.select]
 *        لالتقاط شكل استجابة غير قياسي (مثل chats التي تُرجع data.list)
 */
const DEFAULT_SELECT = (res) => ({
  rows: res?.data?.data ?? [],
  meta: res?.data?.meta,
});

const useTableData = (
  endpoint,
  { params = {}, enabled = true, select = DEFAULT_SELECT, perPage = DEFAULT_PER_PAGE } = {},
) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearchValue] = useState("");
  // order: "ascend" | "descend" | null (متوافق مع antd)
  const [sort, setSort] = useState({ field: null, order: null });
  const [meta, setMeta] = useState({
    total: 0,
    perPage,
    from: undefined,
    to: undefined,
  });

  // نُسلسل الفلاتر حتى لا يُعاد الجلب مع كل إعادة رسم بسبب كائن جديد
  const paramsKey = JSON.stringify(params);

  const buildUrl = useCallback(
    (pageNumber, size = perPage) =>
      `${endpoint}${buildQuery({
        ...JSON.parse(paramsKey),
        page: pageNumber,
        per_page: size,
        search,
        sort_by: sort.order ? sort.field : "",
        sort_dir: sort.order === "ascend" ? "asc" : sort.order ? "desc" : "",
      })}`,
    [endpoint, paramsKey, search, sort, perPage],
  );

  const applyResponse = useCallback(
    (res) => {
      const { rows: nextRows, meta: nextMeta } = select(res);

      setRows(nextRows);
      setMeta({
        // بعض الـ endpoints ترجّع القائمة كاملة بلا meta، فنعتمد عدد الأسطر
        total: nextMeta?.total ?? nextRows.length,
        perPage: Number(nextMeta?.per_page) || perPage,
        from: nextMeta?.from,
        to: nextMeta?.to,
      });
    },
    [select, perPage],
  );

  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    setLoading(true);
    get(buildUrl(page)).then((res) => {
      if (cancelled) return;

      applyResponse(res);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [buildUrl, page, enabled, reloadToken, applyResponse]);

  // البحث والترتيب يغيّران مجموعة النتائج، فنعود لأول صفحة
  const setSearch = useCallback((value) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  const changeSort = useCallback((next) => {
    setSort(next);
    setPage(1);
  }, []);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  /** حذف سطر محلياً بعد نجاح حذفه على الخادم، مع تصحيح العدادات */
  const removeRow = useCallback((rowKey, keyField = "id") => {
    setRows((current) => current.filter((item) => item[keyField] !== rowKey));
    setMeta((current) => ({
      ...current,
      total: Math.max(0, current.total - 1),
      to: current.to ? current.to - 1 : current.to,
    }));
  }, []);

  /** تحديث سطر واحد محلياً (تغيير حالة، تفعيل...) بلا إعادة جلب الصفحة */
  const patchRow = useCallback((rowKey, changes, keyField = "id") => {
    setRows((current) =>
      current.map((item) =>
        item[keyField] === rowKey ? { ...item, ...changes } : item,
      ),
    );
  }, []);

  /** يسحب كل الصفحات بنفس البحث والترتيب الحاليين — يستخدمه التصدير */
  const fetchAllRows = useCallback(async () => {
    const all = [];
    let currentPage = 1;
    let lastPage = 1;

    do {
      const res = await get(buildUrl(currentPage, EXPORT_PER_PAGE));
      const { rows: pageRows, meta: pageMeta } = select(res);

      if (!pageRows.length) break;

      all.push(...pageRows);
      lastPage = pageMeta?.last_page ?? 1;
      currentPage += 1;
    } while (currentPage <= lastPage && currentPage <= MAX_EXPORT_PAGES);

    return all;
  }, [buildUrl, select]);

  return useMemo(
    () => ({
      rows,
      setRows,
      loading,
      meta,
      page,
      setPage,
      search,
      setSearch,
      sort,
      setSort: changeSort,
      reload,
      removeRow,
      patchRow,
      fetchAllRows,
    }),
    [
      rows,
      loading,
      meta,
      page,
      search,
      sort,
      changeSort,
      setSearch,
      reload,
      removeRow,
      patchRow,
      fetchAllRows,
    ],
  );
};

export default useTableData;
